import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";

export type AppErrorContext = "public" | "app";

export interface AppErrorFallbackProps {
  variant: "fullPage" | "inline";
  context?: AppErrorContext;
  title?: string;
  body?: string;
  titleKey?: string;
  bodyKey?: string;
  error?: unknown;
  onReset?: () => void;
  className?: string;
  extraAction?: ReactNode;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return String(error);
}

function detectContext(): AppErrorContext {
  if (typeof window === "undefined") return "public";
  return window.location.pathname.startsWith("/app") ? "app" : "public";
}

export function AppErrorFallback({
  variant,
  context,
  title,
  body,
  titleKey,
  bodyKey,
  error,
  onReset,
  className,
  extraAction,
}: AppErrorFallbackProps) {
  const { t } = useTranslation();
  const resolvedContext = context ?? detectContext();
  const resolvedTitle = title ?? (titleKey ? t(titleKey) : t("errors.boundary.title"));
  const resolvedBody = body ?? (bodyKey ? t(bodyKey) : t("errors.boundary.body"));
  const devMessage = error != null ? errorMessage(error) : "";

  const reloadButton = (
    <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
      {t("errors.actions.reload")}
    </button>
  );

  const homeLink =
    resolvedContext === "app" ? (
      <Link to="/app/dashboard" className="btn btn-ghost">
        {t("errors.actions.dashboard")}
      </Link>
    ) : (
      <Link to="/" className="btn btn-ghost">
        {t("errors.actions.home")}
      </Link>
    );

  const tryAgainButton =
    onReset != null ? (
      <button type="button" className="btn btn-ghost" onClick={onReset}>
        {t("errors.actions.tryAgain")}
      </button>
    ) : null;

  const devDetails =
    import.meta.env.DEV && devMessage ? (
      <details className="app-error__dev-details">
        <summary>{t("errors.devDetails")}</summary>
        <pre>{devMessage}</pre>
      </details>
    ) : null;

  if (variant === "inline") {
    const rootClass = ["app-error-inline", className].filter(Boolean).join(" ");
    return (
      <div className={rootClass} role="alert">
        <h2 className="app-error-inline__title">{resolvedTitle}</h2>
        <p className="app-error-inline__body">{resolvedBody}</p>
        <div className="app-error-inline__actions">
          {tryAgainButton}
          {reloadButton}
          {homeLink}
          {extraAction}
        </div>
        {devDetails}
      </div>
    );
  }

  const rootClass = ["app-error-page", className].filter(Boolean).join(" ");
  return (
    <section className={rootClass} aria-labelledby="app-error-heading">
      <p className="app-error-page__eyebrow">{t("errors.boundary.eyebrow")}</p>
      <h1 id="app-error-heading" className="app-error-page__title">
        {resolvedTitle}
      </h1>
      <p className="app-error-page__lead">{resolvedBody}</p>
      <div className="app-error-page__actions">
        {reloadButton}
        {homeLink}
        {tryAgainButton}
        {extraAction}
      </div>
      {devDetails}
    </section>
  );
}
